
import { useParams } from 'react-router-dom';
import { useAdmin } from '@/contexts/admin/AdminContext';
import { ArticleFormData } from '@/types/article-types';
import { useArticleFormState } from './useArticleFormState';
import { useArticleFormValidation } from './useArticleFormValidation';
import { useArticleDataFetching } from './useArticleDataFetching';
import { useArticleFormSubmission } from './useArticleFormSubmission';
import { useArticleMediaHandling } from './useArticleMediaHandling';
import { useCallback } from 'react';

export const useArticleForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  
  const { 
    articles, 
    loadArticles,
    createArticle,
    updateArticle,
    createArticleTag,
    uploadArticleMedia,
    fetchArticleById
  } = useAdmin();
  
  // Use our custom hooks
  const formState = useArticleFormState(isEditMode);
  
  const { validateForm } = useArticleFormValidation(formState.setFormErrors);
  
  useArticleDataFetching(
    isEditMode,
    id,
    fetchArticleById,
    {
      setTitle: formState.setTitle,
      setLeadText: formState.setLeadText,
      setContent: formState.setContent,
      setHeaderImageUrl: formState.setHeaderImageUrl,
      setAuthorId: formState.setAuthorId,
      setSelectedTags: formState.setSelectedTags,
      setStatus: formState.setStatus,
      setPublishDate: formState.setPublishDate
    },
    formState.setLoading,
    formState.setError
  );
  
  const { handleSubmit, handleCancel } = useArticleFormSubmission(
    isEditMode,
    id,
    createArticle,
    updateArticle,
    loadArticles,
    (formData: ArticleFormData) => validateForm(formData),
    formState.setSaving,
    formState.setError,
    {
      title: formState.title,
      leadText: formState.leadText,
      content: formState.content,
      headerImageUrl: formState.headerImageUrl,
      authorId: formState.authorId,
      selectedTags: formState.selectedTags,
      status: formState.status,
      publishDate: formState.publishDate
    }
  );
  
  const { handleHeaderImageUpload: originalHandleHeaderImageUpload, handleCreateTag } = useArticleMediaHandling(
    uploadArticleMedia,
    createArticleTag
  );
  
  // Create an adapter function that matches the expected void return type
  const handleHeaderImageUpload = useCallback(async (file: File) => {
    const url = await originalHandleHeaderImageUpload(file);
    if (url) {
      formState.setHeaderImageUrl(url);
    }
  }, [originalHandleHeaderImageUpload, formState.setHeaderImageUrl]);

  return {
    isEditMode,
    loading: formState.loading,
    error: formState.error,
    saving: formState.saving,
    formData: {
      articleId: id,
      title: formState.title,
      setTitle: formState.setTitle,
      leadText: formState.leadText,
      setLeadText: formState.setLeadText,
      content: formState.content,
      setContent: formState.setContent,
      headerImageUrl: formState.headerImageUrl,
      setHeaderImageUrl: formState.setHeaderImageUrl,
      authorId: formState.authorId,
      setAuthorId: formState.setAuthorId,
      selectedTags: formState.selectedTags,
      setSelectedTags: formState.setSelectedTags,
      status: formState.status,
      setStatus: formState.setStatus,
      publishDate: formState.publishDate,
      setPublishDate: formState.setPublishDate,
      calendarOpen: formState.calendarOpen,
      setCalendarOpen: formState.setCalendarOpen
    },
    formErrors: formState.formErrors,
    handleSubmit,
    handleCancel,
    handleHeaderImageUpload,
    handleCreateTag
  };
};
